/** Display helpers. Every number an operator reads goes through one of these. */

const UNITS = ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB'];

export function bytes(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  if (value === 0) return '0 B';
  const exponent = Math.min(UNITS.length - 1, Math.floor(Math.log(Math.abs(value)) / Math.log(1024)));
  const scaled = value / 1024 ** exponent;
  return `${scaled.toFixed(exponent === 0 ? 0 : digits)} ${UNITS[exponent]}`;
}

export function percent(value: number | null | undefined, digits = 0): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return `${(value * 100).toFixed(digits)}%`;
}

export function ratio(used: number | null | undefined, total: number | null | undefined): number | null {
  if (used === null || used === undefined || total === null || total === undefined || total <= 0) return null;
  return Math.min(1, Math.max(0, used / total));
}

export function uptime(seconds: number | null | undefined): string {
  if (!seconds || seconds <= 0) return '—';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

export function relativeTime(epochMs: number | null | undefined): string {
  if (!epochMs) return 'never';
  const delta = Math.max(0, Date.now() - epochMs);
  if (delta < 2000) return 'just now';
  if (delta < 60_000) return `${Math.floor(delta / 1000)}s ago`;
  if (delta < 3_600_000) return `${Math.floor(delta / 60_000)}m ago`;
  if (delta < 86_400_000) return `${Math.floor(delta / 3_600_000)}h ago`;
  return `${Math.floor(delta / 86_400_000)}d ago`;
}

/**
 * How long something took: `1h 12m`, `48m`, `35s`.
 *
 * Distinct from `uptime` above, which counts in days because a host's uptime
 * is measured in them. A backup is measured in minutes, and rounding a
 * 40-minute run to "0h" would make the timeline useless.
 */
export function elapsed(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return '—';
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

/** Local wall clock, `03:15`. */
export function clockTime(epochMs: number | null | undefined): string {
  if (!epochMs) return '—';
  return new Date(epochMs).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/** `Sun 31 Aug, 03:15` — for a tooltip, where which night it was matters. */
export function dayTime(epochMs: number | null | undefined): string {
  if (!epochMs) return '—';
  const date = new Date(epochMs);
  const day = date.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
  return `${day}, ${clockTime(epochMs)}`;
}

/** `bc:24:11:a0:01:00` -> `BC:24:11:A0:01:00` for display; storage stays lower. */
export function mac(value: string | null | undefined): string {
  return value ? value.toUpperCase() : '—';
}
